import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { useMemo } from 'react';
import { providerIndexRoute } from './Provider.Index';
import Provider from 'models/productManagement/provider/Model.Provider';
import providerDeleteApi from 'models/productManagement/provider/delete/Api.Provider.Delete';

export enum ProviderAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyProviderAction = Utils.VerifyExtends<
  Module.ProductManagement.Provider.Actions,
  Utils.ValueOf<typeof ProviderAction>
>;
export default function useProvider() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.ProductManagement.Provider.Params, Module.ProductManagement.Provider.Query>({
    route: providerIndexRoute,
  });
  const canView = permissions.CREATE_PROVIDER || permissions.EDIT_PROVIDER || permissions.DELETE_PROVIDER;
  const listApi = useApi({
    action: Provider.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: providerDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    selected,
    permissions,
    views: {
      [ProviderAction.DELETE]: pageRouter.params.action === ProviderAction.DELETE && selected && permissions.DELETE_PROVIDER,
      [ProviderAction.EDIT]:
        pageRouter.params.action === ProviderAction.EDIT &&
        ((permissions.CREATE_PROVIDER && !selected) || (permissions.EDIT_PROVIDER && selected)),
    },
  };
}

export function useProviderProvider() {
  return useDataProvider<ReturnType<typeof useProvider>>();
}
